import { useId } from "react";
import { VAT_SCHEMES } from "@ondo/shared";

import { useModalDialog } from "../lib/useModalDialog.ts";
import { OWNER_SALE_CHANNELS, OWNER_SALE_COPY } from "./ownerSale.constants.ts";
import { OWNER_SALE_STEPS } from "./ownerSale.model.ts";
import type { OwnerSaleDraft } from "./ownerSale.model.ts";
import type { OwnerSaleController } from "./useOwnerSaleController.ts";
import "../lib/dialog.css";
import "./ownerSale.css";

/**
 * Recording a sale of your own work: two steps, in one dialog.
 *
 * A native `<dialog>` opened with `showModal()`, which is the choice every
 * modal in this app makes and for the list of things it does that would
 * otherwise have to be written and got right: modal semantics, Escape, a real
 * focus trap, the rest of the document made INERT rather than merely covered,
 * focus returned to whatever opened it, and the top layer, so there is no
 * portal and no z-index scale to invent.
 *
 * TWO STEPS WHERE THE GALLERY'S WIZARD HAS FIVE, and the three that are gone
 * are each a decision in `20261014093000_an_owner_sells_their_own_work.sql`
 * rather than a simplification: there is no consignment to read terms back off,
 * no covenant this door can attach, and no fee on a sale no firm effected.
 * `ownerSale.model.ts`'s `OwnerSaleStep` carries the argument.
 *
 * THERE IS NO BUYER CHECK ON THE ID FIELD, and that absence has to be visible
 * rather than silent. The gallery's step reads a name back off
 * `ondo.transferee_check()` and gates Continue on the seller agreeing it; that
 * function takes a consignment, so there is nothing to ask here. What stands in
 * its place is `OWNER_SALE_COPY.idHelp`, which says the id is recorded as typed
 * and answered when the sale is recorded — never that it has been checked. Do
 * not add a verdict line, a tick or a name here without a door behind it.
 *
 * The view holds no business condition: the step gate, the read-back and the
 * sentence about what the press does are all on the controller's view-model.
 */
export function OwnerSaleDialog({ controller }: { controller: OwnerSaleController }) {
  const { dialog: view, setField, next, back, submit, close } = controller;
  const ref = useModalDialog(view.open);
  const titleId = useId();
  const base = useId();

  return (
    <dialog
      ref={ref}
      className="ownsale-dialog"
      aria-labelledby={titleId}
      // Escape and the backdrop both close natively; this is what tells React it
      // happened. Without it the two disagree and the next render calls
      // showModal() on a dialog that is already open.
      onClose={close}
    >
      <div className="ownsale-dialog__head ondo-dialog__head">
        <h2 id={titleId} className="ownsale-dialog__title">
          {OWNER_SALE_COPY.dialog.title}
        </h2>
        <button
          type="button"
          className="ondo-dialog__close"
          aria-label={OWNER_SALE_COPY.dialog.close}
          onClick={close}
        >
          ✕
        </button>
      </div>

      {view.open && (
        <>
          <ol className="ownsale-dialog__steps">
            {OWNER_SALE_STEPS.map((step, index) => {
              const state = view.done.includes(step)
                ? "done"
                : step === view.step
                  ? "on"
                  : "ahead";

              return (
                <li
                  key={step}
                  className={`ownsale-dialog__step ownsale-dialog__step--${state}`}
                >
                  <span className="ownsale-dialog__step-mark" aria-hidden="true">
                    {state === "done" ? "✓" : index + 1}
                  </span>
                  <span>{OWNER_SALE_COPY.steps[step]}</span>
                </li>
              );
            })}
          </ol>

          <div className="ownsale-dialog__body">
            {view.step === "terms" ? (
              <TermsStep base={base} draft={view.draft} setField={setField} />
            ) : (
              <>
                <dl className="ownsale-dialog__facts">
                  {view.summary.map((row) => (
                    <div key={row.label} className="ownsale-dialog__fact">
                      <dt>{row.label}</dt>
                      <dd>{row.value}</dd>
                    </div>
                  ))}
                </dl>

                <p className="ownsale-dialog__effect">{view.effect}</p>

                {/* WHAT IS NOT PART OF THIS ACT, on the screen rather than only
                    in a comment: no covenant, and no fee. Anybody who has seen
                    the gallery's wizard will look for both. */}
                <p className="ownsale-dialog__note">{OWNER_SALE_COPY.notPartOfThis}</p>

                <label className="ownsale-dialog__check" htmlFor={`${base}-confirm`}>
                  <input
                    id={`${base}-confirm`}
                    type="checkbox"
                    checked={view.draft.confirmedTitle}
                    onChange={(event) =>
                      setField("confirmedTitle", event.currentTarget.checked)
                    }
                  />
                  <span>{OWNER_SALE_COPY.confirm}</span>
                </label>
              </>
            )}

            {/* WHY THE BUTTON WILL NOT GO, beside it rather than only in its
                disabled state. The model answers with a sentence for exactly
                this; see `problemWith`. It is not an alert — nothing has gone
                wrong, the form is simply not finished — so the refusal below
                keeps `role="alert"` to itself. */}
            {view.problem !== null && view.step === "terms" && (
              <p className="ownsale-dialog__problem">{view.problem}</p>
            )}

            {view.error !== null && (
              <p className="ownsale-dialog__error" role="alert">
                {view.error}
              </p>
            )}
          </div>

          <div className="ownsale-dialog__actions">
            <button
              type="button"
              className="ownsale-dialog__back"
              onClick={back}
              disabled={view.step === "terms" || view.submitting}
            >
              {OWNER_SALE_COPY.dialog.back}
            </button>

            {view.step === "record" ? (
              <button
                type="button"
                className="ownsale-dialog__submit"
                onClick={submit}
                disabled={view.problem !== null || view.submitting}
              >
                {view.submitting
                  ? OWNER_SALE_COPY.dialog.submitting
                  : OWNER_SALE_COPY.dialog.submit}
              </button>
            ) : (
              <button
                type="button"
                className="ownsale-dialog__submit"
                onClick={next}
                disabled={view.problem !== null || view.submitting}
              >
                {OWNER_SALE_COPY.dialog.next}
              </button>
            )}
          </div>
        </>
      )}
    </dialog>
  );
}

type SetField = <K extends keyof OwnerSaleDraft>(key: K, value: OwnerSaleDraft[K]) => void;

/**
 * Who is buying, for how much, how, and when.
 *
 * The toggle decides which field is READ as well as which is shown — see
 * `namedBuyer` in `lib/buyer.ts` — so a name left behind by switching cannot be
 * submitted beside an id. Neither side searches for anybody: an ONDO id is
 * attached, never discovered.
 */
function TermsStep({
  base,
  draft,
  setField,
}: {
  base: string;
  draft: OwnerSaleDraft;
  setField: SetField;
}) {
  return (
    <section>
      <h3 className="ownsale-dialog__legend">{OWNER_SALE_COPY.buyerLegend}</h3>

      <div
        className="ownsale-dialog__toggle"
        role="group"
        aria-label={OWNER_SALE_COPY.buyerLegend}
      >
        <button
          type="button"
          className={`ownsale-dialog__toggle-half${draft.buyerMode === "ondo" ? " ownsale-dialog__toggle-half--on" : ""}`}
          aria-pressed={draft.buyerMode === "ondo"}
          onClick={() => setField("buyerMode", "ondo")}
        >
          {OWNER_SALE_COPY.matchOnNetwork}
        </button>
        <button
          type="button"
          className={`ownsale-dialog__toggle-half${draft.buyerMode === "name" ? " ownsale-dialog__toggle-half--on" : ""}`}
          aria-pressed={draft.buyerMode === "name"}
          onClick={() => setField("buyerMode", "name")}
        >
          {OWNER_SALE_COPY.createNewOwner}
        </button>
      </div>

      {draft.buyerMode === "ondo" ? (
        <>
          <label className="ownsale-dialog__label" htmlFor={`${base}-ondo`}>
            {OWNER_SALE_COPY.idLabel}
          </label>
          <input
            id={`${base}-ondo`}
            className="ownsale-dialog__input"
            value={draft.buyerOndoId}
            placeholder={OWNER_SALE_COPY.idPlaceholder}
            autoComplete="off"
            spellCheck={false}
            onChange={(event) => setField("buyerOndoId", event.currentTarget.value)}
          />
          {/* NO onBlur, and no verdict beneath. There is no `buyer-check` on
              this route; the help text says what that means for the reader. */}
          <p className="ownsale-dialog__help">{OWNER_SALE_COPY.idHelp}</p>
        </>
      ) : (
        <>
          <label className="ownsale-dialog__label" htmlFor={`${base}-name`}>
            {OWNER_SALE_COPY.nameLabel}
          </label>
          <input
            id={`${base}-name`}
            className="ownsale-dialog__input"
            value={draft.buyerName}
            placeholder={OWNER_SALE_COPY.namePlaceholder}
            onChange={(event) => setField("buyerName", event.currentTarget.value)}
          />
          <p className="ownsale-dialog__help">{OWNER_SALE_COPY.nameHelp}</p>

          {/* ONLY ON THIS ARM. The engine refuses an address beside an ONDO id —
              "a buyer named by ONDO id is already here" — so the field is not
              drawn where it could not be sent. */}
          <label className="ownsale-dialog__label" htmlFor={`${base}-email`}>
            {OWNER_SALE_COPY.emailLabel}
          </label>
          <input
            id={`${base}-email`}
            className="ownsale-dialog__input"
            type="email"
            value={draft.buyerEmail}
            placeholder={OWNER_SALE_COPY.emailPlaceholder}
            autoComplete="off"
            onChange={(event) => setField("buyerEmail", event.currentTarget.value)}
          />
          <p className="ownsale-dialog__help">{OWNER_SALE_COPY.emailHelp}</p>
        </>
      )}

      <div className="ownsale-dialog__pair">
        <div>
          <label className="ownsale-dialog__label" htmlFor={`${base}-price`}>
            {OWNER_SALE_COPY.priceLabel}
          </label>
          <input
            id={`${base}-price`}
            className="ownsale-dialog__input"
            inputMode="decimal"
            value={draft.price}
            onChange={(event) => setField("price", event.currentTarget.value)}
          />
        </div>
        <div>
          <label className="ownsale-dialog__label" htmlFor={`${base}-currency`}>
            {OWNER_SALE_COPY.currencyLabel}
          </label>
          <input
            id={`${base}-currency`}
            className="ownsale-dialog__input"
            maxLength={3}
            value={draft.currency}
            onChange={(event) => setField("currency", event.currentTarget.value)}
          />
        </div>
      </div>
      <p className="ownsale-dialog__help">{OWNER_SALE_COPY.priceHelp}</p>

      <label className="ownsale-dialog__label" htmlFor={`${base}-channel`}>
        {OWNER_SALE_COPY.channelLabel}
      </label>
      <select
        id={`${base}-channel`}
        className="ownsale-dialog__input"
        value={draft.channel}
        onChange={(event) =>
          setField("channel", event.currentTarget.value as OwnerSaleDraft["channel"])
        }
      >
        {/* TWO OPTIONS, NOT `TRANSFER_CHANNELS`' THREE. The engine raises a
            22023 on 'gallery' when no firm effected the sale — decision 9 — so
            offering the word would be offering a refusal. */}
        {OWNER_SALE_CHANNELS.map((channel) => (
          <option key={channel} value={channel}>
            {OWNER_SALE_COPY.channel[channel]}
          </option>
        ))}
      </select>
      <p className="ownsale-dialog__help">{OWNER_SALE_COPY.channelHelp}</p>

      <label className="ownsale-dialog__label" htmlFor={`${base}-sold-on`}>
        {OWNER_SALE_COPY.soldOnLabel}
      </label>
      <input
        id={`${base}-sold-on`}
        className="ownsale-dialog__input"
        type="date"
        value={draft.soldOn}
        onChange={(event) => setField("soldOn", event.currentTarget.value)}
      />
      <p className="ownsale-dialog__help">{OWNER_SALE_COPY.soldOnHelp}</p>

      <label className="ownsale-dialog__check" htmlFor={`${base}-vat`}>
        <input
          id={`${base}-vat`}
          type="checkbox"
          checked={draft.addVat}
          onChange={(event) => setField("addVat", event.currentTarget.checked)}
        />
        <span>
          <strong>{OWNER_SALE_COPY.addVat}</strong> — {OWNER_SALE_COPY.addVatHelp}
        </span>
      </label>

      {draft.addVat && (
        <>
          <div className="ownsale-dialog__pair">
            <div>
              <label className="ownsale-dialog__label" htmlFor={`${base}-scheme`}>
                {OWNER_SALE_COPY.vatSchemeLabel}
              </label>
              <select
                id={`${base}-scheme`}
                className="ownsale-dialog__input"
                value={draft.vatScheme}
                onChange={(event) =>
                  setField("vatScheme", event.currentTarget.value as OwnerSaleDraft["vatScheme"])
                }
              >
                {VAT_SCHEMES.map((scheme) => (
                  <option key={scheme} value={scheme}>
                    {OWNER_SALE_COPY.vatScheme[scheme]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="ownsale-dialog__label" htmlFor={`${base}-vat-amount`}>
                {OWNER_SALE_COPY.vatAmountLabel}
              </label>
              <input
                id={`${base}-vat-amount`}
                className="ownsale-dialog__input"
                inputMode="decimal"
                value={draft.vat}
                onChange={(event) => setField("vat", event.currentTarget.value)}
              />
            </div>
          </div>
          <p className="ownsale-dialog__help">{OWNER_SALE_COPY.vatHelp}</p>
        </>
      )}
    </section>
  );
}
